import React from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BootstrapSwitchButton from 'bootstrap-switch-button-react';
import {
  UpdateCarProtection,
  getCarProtectionListing,
  DeleteCarProtectionPackage,
  AddMedia,
} from './../../../../Redux/Actions/UserAction';
import Sidebar from '../../../common/Sidebar/Sidebar';
import { useTranslation } from 'react-i18next';
import { CommonBusinessHeader } from '../CommonBusinessHeader';
import Swal from 'sweetalert2';
const CarProtectionPackages = () => {
  const { t } = useTranslation();
  const home = useSelector((state) => state?.home);
  const packagesAll = home?.getCarProtectionList?.[0]?.packages;
  const getCarProtectionList = home?.getCarProtectionList;

  const dispatch = useDispatch();
  const [CarProtectionData, setCarProtectionData] = useState({
    packageName: '',
    description: '',
    price: '',
  });

  const [edit, setEdit] = useState(false);
  // [CF-16] Drag-and-drop reorder: index of the row currently being dragged.
  const [dragIndex, setDragIndex] = useState(null);
  // Selected packageIds for bulk delete.
  const [selectedIds, setSelectedIds] = useState([]);
  // [CF-16] Local order so a drop reorders instantly (no wait for refetch).
  const [orderList, setOrderList] = useState([]);

  useEffect(() => {
    if (packagesAll === undefined) {
      dispatch(getCarProtectionListing(sessionStorage.getItem('BusinessId')));
    }
  }, []);

  // Keep the local order in sync with the redux source list.
  useEffect(() => {
    setOrderList(Array.isArray(packagesAll) ? packagesAll : []);
  }, [packagesAll]);

  function handleChange(e) {
    const { name, value } = e.target;
    setCarProtectionData((user) => ({ ...user, [name]: value }));
  }

  // Close the Add/Edit Package modal after a successful submit. The modal is
  // opened via legacy data-toggle, so try Bootstrap 5, then jQuery, then a
  // manual DOM teardown as a last resort — whichever is active.
  const closeModal = () => {
    const el = document.getElementById('add-new-car');
    if (!el) return;
    try {
      // eslint-disable-next-line global-require
      const { Modal } = require('bootstrap');
      (Modal.getInstance(el) || Modal.getOrCreateInstance(el)).hide();
    } catch (e) {}
    try {
      if (window.$ && window.$(el).modal) window.$(el).modal('hide');
    } catch (e) {}
    // Fallback: if still visible, tear the modal down manually.
    setTimeout(() => {
      if (el.classList.contains('show') || el.style.display === 'block') {
        el.classList.remove('show');
        el.style.display = 'none';
        el.setAttribute('aria-hidden', 'true');
      }
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('padding-right');
      document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
    }, 60);
  };

  const datasubmit = () => {
    // [CRITICAL] Never submit an empty/incomplete package — the backend rebuilds
    // the whole list (delete-all then re-insert); an invalid package wipes all.
    // NOTE: `Number(price) <= 0` is FALSE for NaN, so a non-numeric price (e.g.
    // "abc") used to slip through and the numeric column rejected the rebuild,
    // deleting every package. Use `!(Number(price) > 0)` so NaN is caught too.
    const priceNum = Number(CarProtectionData?.price);
    if (!CarProtectionData?.packageName?.trim() || !(priceNum > 0)) {
      Swal.fire({
        title: t('Please enter a package name and a valid price'),
        icon: 'error',
        confirmButtonColor: '#3ab1f7',
        confirmButtonText: t('Ok'),
      });
      return;
    }
    // Remove any existing package with the SAME id (an edit) OR the SAME name
    // (re-adding after a Reset, which clears the id) so we never create a
    // duplicate. Store the price as a real number for the rebuild.
    const newName = String(CarProtectionData.packageName).trim().toLowerCase();
    var filtered = (packagesAll || []).filter(
      (data) =>
        data?.packageId !== CarProtectionData?.packageId &&
        String(data?.packageName || '').trim().toLowerCase() !== newName
    );
    const packages = [...filtered, { ...CarProtectionData, price: priceNum }];

    dispatch(UpdateCarProtection({ packages }));
    closeModal();
    // Fully reset so the next open starts clean (no stale packageId that could
    // mis-target the next add/edit).
    setEdit(false);
    setCarProtectionData({ packageName: '', description: '', price: '' });
  };

  // [CF] The in-modal "Reset" clears the visible fields but KEEPS the
  // edit target (packageId) so the user stays editing the SAME package —
  // Reset then re-type must UPDATE it, not create a second one. (The top
  // "Add Package" button uses ResetHendler, which fully starts a new add.)
  const clearForm = () => {
    setCarProtectionData((prev) => ({
      ...prev,
      packageName: '',
      description: '',
      price: '',
    }));
  };

  const ResetHendler = () => {
    setEdit(false);
    setCarProtectionData({ packageName: '', description: '', price: '' });
  };

  const onEditPackage = (pack) => {
    setEdit(true);
    setCarProtectionData({
      ...pack,
    });
  };

  // [CF-15] Confirm before deleting a package (was deleting instantly).
  const onDeletePackage = (packageId) => {
    Swal.fire({
      title: t('Are you sure?'),
      text: t('This package will be permanently deleted.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('Yes, delete it'),
      cancelButtonText: t('Cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(DeleteCarProtectionPackage({ packId: packageId }));
      }
    });
  };

  // [CF-16] Reorder packages via native HTML5 drag-and-drop (arrows removed).
  // Persist explicit serialNumber so the backend (which re-creates packages and
  // orders by serialNumber ASC) keeps the order — same persistence as before.
  const onDropPackage = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }
    const arr = [...orderList];
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(dropIndex, 0, moved);
    const packages = arr.map((p, i) => ({ ...p, serialNumber: i }));
    setOrderList(packages); // instant visual reorder
    setDragIndex(null);
    dispatch(UpdateCarProtection({ packages }, { silent: true })); // persist quietly
  };

  // Toggle a package's checkbox for bulk delete.
  const toggleSelect = (packageId) => {
    setSelectedIds((prev) =>
      prev.includes(packageId)
        ? prev.filter((id) => id !== packageId)
        : [...prev, packageId]
    );
  };

  // Delete all selected packages after a single confirmation. Reuses the
  // existing single-delete action for each selected packageId.
  const onDeleteSelected = () => {
    if (!selectedIds.length) return;
    Swal.fire({
      title: t('Are you sure?'),
      text: t('The selected packages will be permanently deleted.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('Yes, delete them'),
      cancelButtonText: t('Cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        selectedIds.forEach((packageId) => {
          dispatch(DeleteCarProtectionPackage({ packId: packageId }));
        });
        setSelectedIds([]);
      }
    });
  };

  const sideBarOpt = [
    {
      icon: 'assets/images/icons/sp-business/manage-business.svg',
      link: '/sp-car-protection-business',
      label: t('Manage Business'),
    },
    {
      icon: 'assets/images/icons/sp-business/manage-service.svg',
      link: '/sp-car-protection-packages',
      label: t('Manage Packages'),
    },
    {
      icon: 'assets/images/icons/sp-business/banner.svg',
      link: '/sp-car-protection-banner',
      label: t('Banner'),
    },
    {
      icon: 'assets/images/icons/sp-business/manage-working-hours.svg',
      link: '/sp-car-protection-working-hours',
      label: t('Manage Working Hours'),
    },
  ];
  return (
    <div>
      <CommonBusinessHeader
        dataList={getCarProtectionList}
        getNameKey="carProtectionName"
      />
      <div class="main-content-area">
        <div class="container">
          <div class="row justify-content-between">
            <Sidebar options={sideBarOpt} />

            <div class="col-12 col-sm-12 col-md-9 col-lg-9">
              {/* <!-- Add new car modal --> */}
              <div
                class="modal fade"
                id="add-new-car"
                tabindex="-1"
                aria-labelledby="exampleModalLabel"
                aria-hidden="true"
              >
                <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title" id="exampleModalLabel">
                        {t('Add Package')}
                      </h5>
                      <button
                        type="button"
                        class="close"
                        data-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div class="modal-body">
                      <form>
                        <div class="form-group">
                          <label for="cw-package-name">
                            {t('Package Name')}
                          </label>
                          <input
                            name="packageName"
                            onChange={handleChange}
                            value={CarProtectionData.packageName}
                            type="text"
                            class="form-control"
                            id="cw-package-name"
                            placeholder={t('Package Name')}
                          />
                        </div>

                        <div class="form-group">
                          <label for="anc-description">
                            {t('Package Description')}
                          </label>
                          <textarea
                            onChange={handleChange}
                            name="description"
                            value={CarProtectionData.description}
                            class="form-control"
                            id="cw-package-description"
                            rows="3"
                          ></textarea>
                        </div>

                        <div class="form-group">
                          <label for="cw-package-Price">
                            {t('Package Price')}
                          </label>
                          <input
                            onChange={handleChange}
                            name="price"
                            value={CarProtectionData.price}
                            type="number"
                            min="0"
                            step="0.001"
                            class="form-control"
                            id="cw-package-Price"
                            placeholder={t('Package Price')}
                          />
                        </div>
                      </form>
                    </div>

                    <div class="modal-footer">
                      <button
                        onClick={clearForm}
                        type="button"
                        class="btn btn-secondary btn-lg"
                        // href='javascript:void(0);'
                        // role='button'
                      >
                        {t('Reset')}
                      </button>
                      <button
                        onClick={datasubmit}
                        type="button"
                        aria-label="Close"
                        class="btn btn-warning btn-lg "
                      >
                        {edit ? t('Update') : t('Add')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* <!-- Add new car modal --> */}

              <div class="row mb-4 align-items-center justify-content-between">
                <div class="col">
                  <h3 class="mb-0">{t('Manage Packages')}</h3>
                </div>
                <div class="col-auto">
                  {selectedIds.length ? (
                    <button
                      type="button"
                      class="btn btn-danger mr-2"
                      onClick={onDeleteSelected}
                    >
                      <i class="far fa-trash-alt"></i>{' '}
                      {t('Delete selected')} ({selectedIds.length})
                    </button>
                  ) : null}
                  <a
                    class="btn btn-outline-secondary"
                    href="javascript:void(0);"
                    role="button"
                    data-toggle="modal"
                    data-target="#add-new-car"
                    onClick={ResetHendler}
                  >
                    {t('Add Package')}
                  </a>
                </div>
              </div>

              {packagesAll !== undefined && packagesAll.length
                ? orderList.map((item, index) => {
                    return (
                      <>
                        {' '}
                        <div
                          class="sp-cw-package-row"
                          draggable={true}
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', '');
                            setDragIndex(index);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            onDropPackage(index);
                          }}
                          onDragEnd={() => setDragIndex(null)}
                          style={{
                            cursor: 'move',
                            opacity: dragIndex === index ? 0.5 : 1,
                          }}
                        >
                          <div class="row justify-content-between align-items-center">
                            <div class="col-12 col-sm-12 col-md-6 mb-3 mb-sm-3 mb-md-0">
                              <div class="row align-items-center">
                                {/* [CF-16] Drag handle hint */}
                                <div class="col-auto" title={t('Drag to reorder')}>
                                  <i class="fas fa-grip-vertical"></i>
                                </div>
                                {/* Multi-select for bulk delete */}
                                <div class="col-auto d-flex align-items-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(
                                      item?.packageId
                                    )}
                                    onChange={() =>
                                      toggleSelect(item?.packageId)
                                    }
                                    style={{
                                      margin: 0,
                                      width: '16px',
                                      height: '16px',
                                      cursor: 'pointer',
                                      accentColor: '#f7b500',
                                    }}
                                  />
                                </div>
                                <div class="col-auto">
                                  <div class="line-a">{item?.packageName}</div>
                                  {/* <div class='line-a'>{item?.description}</div> */}
                                </div>
                                <div class="col-auto">
                                  <div class="line-b">
                                    {(item?.currency || 'KWD').toUpperCase()}{' '}
                                    {(Number(item?.price) || 0).toFixed(3)}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="col-12 col-sm-12 col-md-auto">
                              <div class="row align-items-center">
                                <div
                                  class="col-auto"
                                  data-toggle="modal"
                                  data-target="#add-new-car"
                                >
                                  <a
                                    href="javascript:void(0);"
                                    role="button"
                                    onClick={() => onEditPackage(item)}
                                  >
                                    <i class="far fa-edit"></i> {t('Edit')}
                                  </a>
                                </div>
                                <div class="col-auto">
                                  <a
                                    href="javascript:void(0);"
                                    onClick={() =>
                                      onDeletePackage(item?.packageId)
                                    }
                                  >
                                    <i class="far fa-trash-alt"></i>{' '}
                                    {t('Delete')}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })
                : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarProtectionPackages;
